/**
 * Profile Image Cache Service
 * Caches profile image URLs to avoid repeated API calls
 * 
 * Cache Strategy:
 * - In-memory cache for fast access during session
 * - sessionStorage for persistence across page refreshes
 * - Separate cache entries for S3 paths (long-term) and presigned URLs (short-term with TTL)
 */

class ProfileImageCache {
  constructor() {
    // In-memory cache: Map<userId, { url: string, timestamp: number, s3Path: string }>
    this.memoryCache = new Map();
    
    // Cache configuration
    this.config = {
      // Presigned URLs expire after 50 minutes (they're usually valid for 1 hour)
      PRESIGNED_URL_TTL: 50 * 60 * 1000, // 50 minutes in milliseconds
      // S3 paths don't expire, but we'll cache them for 24 hours
      S3_PATH_TTL: 24 * 60 * 60 * 1000, // 24 hours
      // Max cache size to prevent memory issues
      MAX_CACHE_SIZE: 500,
      // Storage key for sessionStorage
      STORAGE_KEY: 'profileImageCache',
    };
    
    // Load cache from sessionStorage on initialization
    this.loadFromStorage();
  }

  /**
   * Get cache key for a user
   */
  getCacheKey(userId) {
    return `profile_${userId}`;
  }

  /**
   * Get cache key for an S3 path
   */
  getS3PathCacheKey(s3Path) {
    return `s3path_${s3Path}`;
  }

  /**
   * Load cache from sessionStorage
   */
  loadFromStorage() {
    try {
      const stored = sessionStorage.getItem(this.config.STORAGE_KEY);
      if (stored) {
        const cacheData = JSON.parse(stored);
        const now = Date.now();
        
        // Load valid entries only
        for (const [key, value] of Object.entries(cacheData)) {
          // Check if entry is still valid
          const age = now - value.timestamp;
          const ttl = value.isPresignedUrl 
            ? this.config.PRESIGNED_URL_TTL 
            : this.config.S3_PATH_TTL;
          
          if (age < ttl) {
            this.memoryCache.set(key, value);
          }
        }
        
        console.log(`📦 Loaded ${this.memoryCache.size} cached profile images from storage`);
      }
    } catch (error) {
      console.warn('⚠️ Failed to load profile image cache from storage:', error);
    }
  }

  /**
   * Save cache to sessionStorage
   */
  saveToStorage() {
    try {
      const cacheData = {};
      this.memoryCache.forEach((value, key) => {
        cacheData[key] = value;
      });
      
      sessionStorage.setItem(this.config.STORAGE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('⚠️ Failed to save profile image cache to storage:', error);
      // If storage is full, clear old entries
      this.clearOldEntries();
    }
  }

  /**
   * Check if a cache entry is valid
   */
  isValid(entry, isPresignedUrl = false) {
    if (!entry) return false;
    
    const now = Date.now();
    const age = now - entry.timestamp;
    const ttl = isPresignedUrl 
      ? this.config.PRESIGNED_URL_TTL 
      : this.config.S3_PATH_TTL;
    
    return age < ttl;
  }

  /**
   * Get cached profile image URL
   * @param {string} userId - User ID or S3 path
   * @param {boolean} isS3Path - Whether the key is an S3 path
   * @returns {string|null} - Cached URL or null if not found/invalid
   */
  get(userId, isS3Path = false) {
    if (!userId) return null;
    
    const key = isS3Path ? this.getS3PathCacheKey(userId) : this.getCacheKey(userId);
    const entry = this.memoryCache.get(key);
    
    if (!entry || !entry.url) return null;
    
    // Check if entry is still valid
    const isPresignedUrl = entry.url && entry.url.startsWith('http') && !entry.url.includes('s3://');
    if (!this.isValid(entry, isPresignedUrl)) {
      // Cache expired, remove it
      this.memoryCache.delete(key);
      this.saveToStorage();
      return null;
    }
    
    console.log(`✅ Cache hit for ${isS3Path ? 'S3 path' : 'user'} ${userId}`);
    return entry.url;
  }

  /**
   * Get cached S3 path (doesn't expire as quickly)
   * @param {string} userId - User ID
   * @returns {string|null} - Cached S3 path or null
   */
  getS3Path(userId) {
    if (!userId) return null;
    
    const key = this.getCacheKey(userId);
    const entry = this.memoryCache.get(key);
    
    if (!entry || !entry.s3Path) return null;
    
    // S3 paths have longer TTL
    if (!this.isValid(entry, false)) {
      this.memoryCache.delete(key);
      this.saveToStorage();
      return null;
    }
    
    return entry.s3Path;
  }

  /**
   * Set cached profile image URL
   * @param {string} userId - User ID or S3 path
   * @param {string} url - Profile image URL (presigned URL or direct URL)
   * @param {string} s3Path - Optional S3 path for future reference
   * @param {boolean} isS3Path - Whether userId is actually an S3 path
   */
  set(userId, url, s3Path = null, isS3Path = false) {
    if (!userId || !url) return;
    
    const key = isS3Path ? this.getS3PathCacheKey(userId) : this.getCacheKey(userId);
    
    // Prevent cache from growing too large
    if (this.memoryCache.size >= this.config.MAX_CACHE_SIZE) {
      this.clearOldEntries();
    }
    
    const entry = {
      url: url,
      s3Path: s3Path || (isS3Path ? userId : null),
      timestamp: Date.now(),
      isPresignedUrl: url.startsWith('http') && !url.includes('s3://'),
    };
    
    this.memoryCache.set(key, entry);
    this.saveToStorage();
    
    // If we have both userId and s3Path, also cache with the other key
    if (!isS3Path && s3Path) {
      const s3Key = this.getS3PathCacheKey(s3Path);
      this.memoryCache.set(s3Key, entry);
    }
    
    console.log(`💾 Cached profile image for ${isS3Path ? 'S3 path' : 'user'} ${userId}`);
  }

  /**
   * Set cached S3 path (for when we know the path but haven't converted to presigned URL yet)
   * @param {string} userId - User ID
   * @param {string} s3Path - S3 path
   */
  setS3Path(userId, s3Path) {
    if (!userId || !s3Path) return;
    
    const key = this.getCacheKey(userId);
    const existing = this.memoryCache.get(key);
    
    // Update or create entry
    const entry = existing || {
      url: null,
      timestamp: Date.now(),
      isPresignedUrl: false,
    };
    
    entry.s3Path = s3Path;
    entry.timestamp = Date.now(); // Update timestamp
    
    this.memoryCache.set(key, entry);
    this.saveToStorage();
  }

  /**
   * Clear old entries from cache
   */
  clearOldEntries() {
    const now = Date.now();
    let cleared = 0;
    
    for (const [key, entry] of this.memoryCache.entries()) {
      const isPresignedUrl = entry.isPresignedUrl;
      const ttl = isPresignedUrl 
        ? this.config.PRESIGNED_URL_TTL 
        : this.config.S3_PATH_TTL;
      
      if (now - entry.timestamp >= ttl) {
        this.memoryCache.delete(key);
        cleared++;
      }
    }
    
    if (cleared > 0) {
      console.log(`🧹 Cleared ${cleared} expired cache entries`);
      this.saveToStorage();
    }
    
    // If still too large, remove oldest entries
    if (this.memoryCache.size >= this.config.MAX_CACHE_SIZE) {
      const entries = Array.from(this.memoryCache.entries())
        .sort((a, b) => a[1].timestamp - b[1].timestamp);
      
      const toRemove = entries.slice(0, this.memoryCache.size - this.config.MAX_CACHE_SIZE + 50);
      toRemove.forEach(([key]) => this.memoryCache.delete(key));
      
      console.log(`🧹 Removed ${toRemove.length} oldest cache entries`);
      this.saveToStorage();
    }
  }

  /**
   * Clear specific user from cache
   * @param {string} userId - User ID to clear
   */
  clear(userId) {
    if (!userId) return;
    
    const key = this.getCacheKey(userId);
    this.memoryCache.delete(key);
    this.saveToStorage();
    console.log(`🗑️ Cleared cache for user ${userId}`);
  }

  /**
   * Clear all cached images
   */
  clearAll() {
    this.memoryCache.clear();
    try {
      sessionStorage.removeItem(this.config.STORAGE_KEY);
    } catch (error) {
      console.warn('⚠️ Failed to clear cache from storage:', error);
    }
    console.log('🗑️ Cleared all profile image cache');
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      size: this.memoryCache.size,
      maxSize: this.config.MAX_CACHE_SIZE,
    };
  }
}

// Export singleton instance
export default new ProfileImageCache();

